import React, { useEffect, useState } from 'react';
import {
    DatabaseSoundVocabularyModel,
    DatabaseSoundVocabularyTerm,
    SoundVocabularyKind,
} from '../../services/DatabaseSoundVocabulary';
import { DatabaseSoundPackSoundsModel } from '../../services/DatabaseSoundPackSounds';
import { useSoundPacks } from '../../context/SoundPacksContext';
import { slugify, isValidSlug } from '../../helpers/slug';

const KINDS: { kind: SoundVocabularyKind; title: string; singular: string }[] = [
    { kind: 'instrumentType', title: 'Instrument Types', singular: 'instrument type' },
    { kind: 'tag', title: 'Tags', singular: 'tag' },
];

type TermsByKind = Record<SoundVocabularyKind, DatabaseSoundVocabularyTerm[]>;
type NewTermInput = { label: string; id: string; idTouched: boolean };

// One vocabulary id referenced by sounds but absent from the draft — i.e. it
// would be stale after publishing.
type StaleRef = {
    kind: SoundVocabularyKind;
    id: string;
    soundCount: number;
    packIds: string[];
};

const emptyNewTerm: NewTermInput = { label: '', id: '', idTouched: false };

function TagCollectionEditor() {
    const { soundPacks } = useSoundPacks();

    // Server truth per kind (null until the first snapshot arrives) and the
    // local editable draft. The draft re-seeds from the server whenever there
    // are no unpublished edits, so background changes flow in without ever
    // clobbering in-progress work.
    const [serverTerms, setServerTerms] = useState<Record<SoundVocabularyKind, DatabaseSoundVocabularyTerm[] | null>>({
        tag: null,
        instrumentType: null,
    });
    const [draft, setDraft] = useState<TermsByKind>({ tag: [], instrumentType: [] });
    const [removed, setRemoved] = useState<Record<SoundVocabularyKind, string[]>>({ tag: [], instrumentType: [] });
    const [dirty, setDirty] = useState(false);

    const [newTerm, setNewTerm] = useState<Record<SoundVocabularyKind, NewTermInput>>({
        tag: emptyNewTerm,
        instrumentType: emptyNewTerm,
    });

    const [publishing, setPublishing] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    // Stale references awaiting the admin's "publish anyway" confirmation.
    const [pendingStaleRefs, setPendingStaleRefs] = useState<StaleRef[] | null>(null);
    const [copiedId, setCopiedId] = useState<string | null>(null);

    // Raw comma-separated text of an in-progress aliases edit, keyed by
    // "kind:id". Kept separate from the draft term so typing a trailing
    // comma/space isn't immediately collapsed away by the array round-trip;
    // committed into the draft on blur/Enter.
    const [aliasDrafts, setAliasDrafts] = useState<Record<string, string>>({});
    const aliasDraftKey = (kind: SoundVocabularyKind, id: string) => `${kind}:${id}`;

    useEffect(() => {
        const unsubs = KINDS.map(({ kind }) =>
            DatabaseSoundVocabularyModel.subscribeTerms(
                kind,
                (terms) => setServerTerms(prev => ({ ...prev, [kind]: terms })),
                (error) => setStatusMessage(`Failed to load ${kind} vocabulary: ${error.message}`),
            )
        );
        return () => unsubs.forEach(unsub => unsub());
    }, []);

    useEffect(() => {
        if (dirty || !serverTerms.tag || !serverTerms.instrumentType) return;
        setDraft({ tag: serverTerms.tag, instrumentType: serverTerms.instrumentType });
        setRemoved({ tag: [], instrumentType: [] });
    }, [serverTerms, dirty]);

    const isLoading = !serverTerms.tag || !serverTerms.instrumentType;

    const updateDraft = (kind: SoundVocabularyKind, updater: (terms: DatabaseSoundVocabularyTerm[]) => DatabaseSoundVocabularyTerm[]) => {
        setDraft(prev => ({ ...prev, [kind]: updater(prev[kind]) }));
        setDirty(true);
        setPendingStaleRefs(null);
        setStatusMessage('');
    };

    const handleLabelChange = (kind: SoundVocabularyKind, id: string, label: string) => {
        updateDraft(kind, terms => terms.map(t => (t.id === id ? { ...t, label } : t)));
    };

    const handleAliasesTextChange = (kind: SoundVocabularyKind, id: string, text: string) => {
        setAliasDrafts(prev => ({ ...prev, [aliasDraftKey(kind, id)]: text }));
    };

    const commitAliases = (kind: SoundVocabularyKind, id: string) => {
        const key = aliasDraftKey(kind, id);
        const raw = aliasDrafts[key];
        if (raw === undefined) return;
        const aliases = raw.split(',').map(alias => alias.trim()).filter(Boolean);
        updateDraft(kind, terms => terms.map(t => (t.id === id ? { ...t, aliases } : t)));
        setAliasDrafts(prev => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    const handleMove = (kind: SoundVocabularyKind, index: number, delta: -1 | 1) => {
        updateDraft(kind, terms => {
            const target = index + delta;
            if (target < 0 || target >= terms.length) return terms;
            const next = [...terms];
            [next[index], next[target]] = [next[target], next[index]];
            return next;
        });
    };

    const handleRemove = (kind: SoundVocabularyKind, id: string) => {
        updateDraft(kind, terms => terms.filter(t => t.id !== id));
        if (serverTerms[kind]?.some(t => t.id === id)) {
            setRemoved(prev => ({ ...prev, [kind]: [...prev[kind], id] }));
        }
    };

    const handleNewTermChange = (kind: SoundVocabularyKind, patch: Partial<NewTermInput>) => {
        setNewTerm(prev => {
            const merged = { ...prev[kind], ...patch };
            // Until the admin touches the id field, keep it derived from the label.
            if (!merged.idTouched) merged.id = slugify(merged.label);
            return { ...prev, [kind]: merged };
        });
    };

    const newTermError = (kind: SoundVocabularyKind): string | null => {
        const { label, id } = newTerm[kind];
        if (!label.trim() && !id) return null;
        if (!id) return null;
        if (!isValidSlug(id)) return 'Id must be a kebab-case slug (lowercase letters, digits, dashes)';
        if (draft[kind].some(t => t.id === id)) return `"${id}" already exists`;
        return null;
    };

    const canAdd = (kind: SoundVocabularyKind) =>
        !!newTerm[kind].label.trim() && !!newTerm[kind].id && !newTermError(kind);

    const handleAdd = (kind: SoundVocabularyKind) => {
        if (!canAdd(kind)) return;
        const { label, id } = newTerm[kind];
        updateDraft(kind, terms => [
            ...terms,
            { id, label: label.trim(), active: true, sortOrder: terms.length, aliases: [] },
        ]);
        // Re-adding an id scheduled for deletion cancels the deletion.
        setRemoved(prev => ({ ...prev, [kind]: prev[kind].filter(r => r !== id) }));
        setNewTerm(prev => ({ ...prev, [kind]: emptyNewTerm }));
    };

    const handleCopyId = async (id: string) => {
        try {
            await navigator.clipboard.writeText(id);
            setCopiedId(id);
            setTimeout(() => setCopiedId(current => (current === id ? null : current)), 1500);
        } catch (error) {
            console.error('Failed to copy id:', error);
        }
    };

    /**
     * Scan every sound_packs_sounds document for tag / instrument-type ids
     * that would not exist after publishing the current draft.
     */
    const findStaleRefs = async (): Promise<StaleRef[]> => {
        const allPackSounds = await DatabaseSoundPackSoundsModel.getAllPackSounds();
        const known: Record<SoundVocabularyKind, Set<string>> = {
            tag: new Set(draft.tag.map(t => t.id)),
            instrumentType: new Set(draft.instrumentType.map(t => t.id)),
        };
        const found = new Map<string, StaleRef>();
        for (const pack of allPackSounds) {
            for (const sound of pack.sounds) {
                const collect = (kind: SoundVocabularyKind, ids: string[]) => {
                    for (const id of ids) {
                        if (!id || known[kind].has(id)) continue;
                        const key = `${kind}:${id}`;
                        const entry = found.get(key) ?? { kind, id, soundCount: 0, packIds: [] };
                        entry.soundCount += 1;
                        if (!entry.packIds.includes(pack.packId)) entry.packIds.push(pack.packId);
                        found.set(key, entry);
                    }
                };
                collect('tag', sound.tagIds);
                collect('instrumentType', sound.instrumentTypeIds);
            }
        }
        return Array.from(found.values()).sort((a, b) => a.kind.localeCompare(b.kind) || a.id.localeCompare(b.id));
    };

    const commitPublish = async () => {
        setPublishing(true);
        setStatusMessage('Publishing...');
        try {
            for (const { kind } of KINDS) {
                await DatabaseSoundVocabularyModel.publishTerms(kind, draft[kind], removed[kind]);
            }
            setPendingStaleRefs(null);
            setRemoved({ tag: [], instrumentType: [] });
            setDirty(false); // draft re-seeds from the next server snapshot
            setStatusMessage('Published successfully.');
        } catch (error) {
            console.error('Error publishing vocabulary:', error);
            setStatusMessage('Failed to publish: ' + (error instanceof Error ? error.message : 'Unknown error'));
        } finally {
            setPublishing(false);
        }
    };

    const handlePublish = async () => {
        for (const { kind, singular } of KINDS) {
            if (draft[kind].some(t => !t.label.trim())) {
                setStatusMessage(`Every ${singular} needs a non-empty label.`);
                return;
            }
        }
        setPublishing(true);
        setStatusMessage('Checking sound references...');
        try {
            const stale = await findStaleRefs();
            if (stale.length > 0) {
                setPendingStaleRefs(stale);
                setStatusMessage('');
                return;
            }
            await commitPublish();
        } catch (error) {
            console.error('Error checking sound references:', error);
            setStatusMessage('Stale-reference check failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
        } finally {
            setPublishing(false);
        }
    };

    const packName = (packId: string) => soundPacks.find(p => p.id === packId)?.name ?? packId;

    const removedCount = removed.tag.length + removed.instrumentType.length;

    if (isLoading) {
        return (
            <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {KINDS.map(({ kind, title, singular }) => (
                <div key={kind}>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">{title}</h3>
                    {draft[kind].length === 0 ? (
                        <p className="text-sm text-gray-400 mb-3">No {title.toLowerCase()} defined.</p>
                    ) : (
                        <ul className="space-y-2 mb-3">
                            {draft[kind].map((term, index) => {
                                const isNew = !serverTerms[kind]?.some(t => t.id === term.id);
                                return (
                                    <li key={term.id} className="flex flex-col gap-1.5 border border-gray-200 rounded-md px-3 py-2">
                                        <div className="flex items-center gap-2">
                                            <div className="flex flex-col">
                                                <button
                                                    onClick={() => handleMove(kind, index, -1)}
                                                    disabled={index === 0 || publishing}
                                                    title="Move up"
                                                    className="text-gray-400 hover:text-gray-700 disabled:opacity-30 leading-none text-xs"
                                                >
                                                    ▲
                                                </button>
                                                <button
                                                    onClick={() => handleMove(kind, index, 1)}
                                                    disabled={index === draft[kind].length - 1 || publishing}
                                                    title="Move down"
                                                    className="text-gray-400 hover:text-gray-700 disabled:opacity-30 leading-none text-xs"
                                                >
                                                    ▼
                                                </button>
                                            </div>
                                            <input
                                                type="text"
                                                value={term.label}
                                                onChange={(e) => handleLabelChange(kind, term.id, e.target.value)}
                                                disabled={publishing}
                                                className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                                            />
                                            <code className="bg-gray-100 px-2 py-1 rounded text-xs text-gray-600">{term.id}</code>
                                            <button
                                                onClick={() => handleCopyId(term.id)}
                                                title="Copy id"
                                                className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
                                            >
                                                {copiedId === term.id ? 'Copied!' : 'Copy id'}
                                            </button>
                                            {isNew && (
                                                <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">new</span>
                                            )}
                                            <button
                                                onClick={() => handleRemove(kind, term.id)}
                                                disabled={publishing}
                                                title={`Remove ${singular}`}
                                                className="text-xs px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                        <div className="flex items-center gap-2 pl-7">
                                            <span className="text-xs text-gray-400 shrink-0">Aliases</span>
                                            <input
                                                type="text"
                                                value={aliasDrafts[aliasDraftKey(kind, term.id)] ?? term.aliases.join(', ')}
                                                onChange={(e) => handleAliasesTextChange(kind, term.id, e.target.value)}
                                                onBlur={() => commitAliases(kind, term.id)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                                }}
                                                placeholder="comma-separated synonyms, e.g. kick, bass drum"
                                                disabled={publishing}
                                                className="flex-1 px-3 py-1 border border-gray-200 rounded-md text-xs focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    {/* Add a new term. The id is derived from the label but can be
                        overridden before adding; it is immutable afterwards. */}
                    <div className="flex items-start gap-2">
                        <input
                            type="text"
                            value={newTerm[kind].label}
                            onChange={(e) => handleNewTermChange(kind, { label: e.target.value })}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(kind); }}
                            placeholder={`New ${singular} label`}
                            disabled={publishing}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                        <input
                            type="text"
                            value={newTerm[kind].id}
                            onChange={(e) => handleNewTermChange(kind, { id: e.target.value, idTouched: true })}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(kind); }}
                            placeholder="slug-id"
                            disabled={publishing}
                            className="w-48 px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:ring-blue-500 focus:border-blue-500"
                        />
                        <button
                            onClick={() => handleAdd(kind)}
                            disabled={!canAdd(kind) || publishing}
                            className={`px-4 py-2 rounded-md text-sm font-medium ${canAdd(kind) && !publishing
                                ? 'bg-blue-600 text-white hover:bg-blue-700'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                        >
                            Add
                        </button>
                    </div>
                    {newTermError(kind) && (
                        <p className="text-red-600 font-medium text-xs mt-1">{newTermError(kind)}</p>
                    )}
                </div>
            ))}

            {/* Stale-reference warning: sounds still pointing at ids that would
                no longer exist after this publish. */}
            {pendingStaleRefs && (
                <div className="border border-amber-300 bg-amber-50 rounded-md p-4">
                    <p className="text-sm font-semibold text-amber-800 mb-2">
                        ⚠️ {pendingStaleRefs.length} vocabulary id{pendingStaleRefs.length === 1 ? ' is' : 's are'} still referenced by sounds but won't exist after publishing:
                    </p>
                    <ul className="space-y-1 mb-3">
                        {pendingStaleRefs.map(ref => (
                            <li key={`${ref.kind}:${ref.id}`} className="text-xs text-amber-800">
                                <code className="bg-amber-100 px-1.5 py-0.5 rounded">{ref.id}</code>
                                {' '}({ref.kind === 'tag' ? 'tag' : 'instrument type'}) — {ref.soundCount} sound{ref.soundCount === 1 ? '' : 's'} in {ref.packIds.map(packName).join(', ')}
                            </li>
                        ))}
                    </ul>
                    <p className="text-xs text-amber-700 mb-3">
                        Those sounds will keep the stale ids until their packs are re-published. Publish anyway?
                    </p>
                    <div className="flex gap-2">
                        <button
                            onClick={() => commitPublish()}
                            disabled={publishing}
                            className="px-4 py-2 rounded-md text-sm font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                        >
                            Publish anyway
                        </button>
                        <button
                            onClick={() => setPendingStaleRefs(null)}
                            disabled={publishing}
                            className="px-4 py-2 rounded-md text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            <div className="border-t border-gray-200 pt-4 flex items-center gap-4">
                <button
                    onClick={handlePublish}
                    disabled={!dirty || publishing || !!pendingStaleRefs}
                    className={`px-6 py-2 rounded-md text-sm font-medium ${dirty && !publishing && !pendingStaleRefs
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                >
                    {publishing ? (statusMessage || 'Publishing...') : 'Publish'}
                </button>
                {dirty && !publishing && (
                    <span className="text-sm text-gray-500">
                        Unpublished changes{removedCount > 0 ? ` — ${removedCount} term${removedCount === 1 ? '' : 's'} will be deleted` : ''}
                    </span>
                )}
                {!publishing && statusMessage && (
                    <span className={`text-sm ${statusMessage.startsWith('Published') ? 'text-green-600' : 'text-red-600'}`}>
                        {statusMessage}
                    </span>
                )}
            </div>
        </div>
    );
}

export default TagCollectionEditor;
